import React from "react";
import { ArrowDown, ShieldCheck, Repeat } from "lucide-react";
import { col } from "./kit";

const TONES = ["primary", "info", "accent", "warning", "success", "critical"];
const chip = "text-[10.5px] px-1.5 py-0.5 border border-border rounded-sm bg-surface-2 leading-tight";

// Implementation phases top to bottom: goal, outputs and the exit gate that closes each one.
export function Phases({ phases }) {
  return (
    <ol data-testid="kc-diagram-phases" className="space-y-2 min-w-[560px]">
      {phases.map((p, i) => {
        const tone = p.tone || TONES[i % TONES.length];
        return (
          <li key={p.title} data-testid={`kc-phase-${i}`} className="grid grid-cols-[2.25rem_minmax(0,1fr)_minmax(0,13rem)] gap-3">
            <div className="flex flex-col items-center">
              <span className="w-8 h-8 rounded-full font-mono-tabular font-bold text-[12px] flex items-center justify-center shrink-0"
                    style={{ background: col(tone, 0.15), color: col(tone) }}>{i}</span>
              {i < phases.length - 1 && <span className="flex-1 w-px mt-1" style={{ background: col("border") }} />}
            </div>
            <div className="border rounded-sm p-3 bg-bg/60" style={{ borderColor: col(tone, 0.45) }}>
              <div className="text-[13px] font-semibold leading-tight">{p.title}</div>
              <div className="text-[11.5px] text-muted mt-1 leading-snug">{p.goal}</div>
              <div className="mt-2 flex flex-wrap gap-1">{p.outputs.map(o => <span key={o} className={chip}>{o}</span>)}</div>
            </div>
            <div className="border border-dashed rounded-sm p-3 text-[11.5px] leading-snug" style={{ borderColor: col("success", 0.5) }}>
              <div className="text-[9.5px] uppercase tracking-widest font-semibold inline-flex items-center gap-1" style={{ color: col("success") }}><ShieldCheck size={10} />Exit gate</div>
              <div className="mt-1">{p.gate}</div>
            </div>
          </li>
        );
      })}
    </ol>
  );
}

const RACI = { R: ["Responsible", "primary"], A: ["Accountable", "accent"], C: ["Consulted", "info"], I: ["Informed", "muted"] };

// Who is Responsible, Accountable, Consulted or Informed in each phase.
export function Raci({ roles, rows }) {
  return (
    <div data-testid="kc-diagram-raci" className="min-w-[620px]">
      <table className="w-full text-[11.5px]">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-1.5 pr-2 text-[10.5px] uppercase tracking-wider text-muted font-semibold">Phase</th>
            {roles.map(r => <th key={r} className="px-1 py-1.5 text-[10.5px] text-muted font-medium leading-tight text-center align-bottom">{r}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.phase} data-testid={`kc-raci-${i}`} className="border-b border-border/60">
              <td className="py-1.5 pr-2 font-medium whitespace-nowrap"><span className="font-mono-tabular text-muted mr-1.5">{i}</span>{row.phase}</td>
              {row.cells.map((c, j) => (
                <td key={j} className="text-center py-1.5">
                  <span title={RACI[c][0]} className="inline-flex w-6 h-6 items-center justify-center rounded-sm font-mono-tabular font-bold text-[11px]"
                        style={{ background: col(RACI[c][1], 0.14), color: col(RACI[c][1]) }}>{c}</span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="mt-2.5 flex flex-wrap gap-3 text-[11px] text-muted">
        {Object.entries(RACI).map(([k, [label, tone]]) => (
          <span key={k} className="inline-flex items-center gap-1.5"><span className="w-4 h-4 rounded-sm font-mono-tabular font-bold text-[10px] inline-flex items-center justify-center" style={{ background: col(tone, 0.14), color: col(tone) }}>{k}</span>{label}</span>
        ))}
      </div>
    </div>
  );
}

// Layered architecture: data flows down through the layers; cross-cutting services on the right.
export function Layers({ layers, rails }) {
  return (
    <div data-testid="kc-diagram-layers" className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_12.5rem] gap-3 min-w-[560px]">
      <div>
        {layers.map((l, i) => (
          <React.Fragment key={l.title}>
            <div data-testid={`kc-layer-${i}`} className="border rounded-sm px-3 py-2.5 grid grid-cols-[8.5rem_minmax(0,1fr)] gap-3 items-center"
                 style={{ borderColor: col(l.tone, 0.45), background: col(l.tone, 0.06) }}>
              <div>
                <div className="text-[12.5px] font-semibold leading-tight" style={{ color: col(l.tone) }}>{l.title}</div>
                <div className="text-[10.5px] text-muted leading-snug mt-0.5">{l.note}</div>
              </div>
              <div className="flex flex-wrap gap-1">{l.items.map(x => <span key={x} className={chip}>{x}</span>)}</div>
            </div>
            {i < layers.length - 1 && <div className="flex justify-center py-0.5 text-subtle"><ArrowDown size={12} /></div>}
          </React.Fragment>
        ))}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-1 gap-2 content-start">
        {rails.map(r => (
          <div key={r.title} className="border border-dashed border-border rounded-sm px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-widest font-semibold text-muted">{r.title}</div>
            <ul className="mt-1.5 space-y-1 text-[11px] leading-snug">{r.items.map(x => <li key={x}>{x}</li>)}</ul>
          </div>
        ))}
      </div>
    </div>
  );
}

// Process swimlane: one column per role, steps top to bottom in order.
export function Swimlane({ lanes, steps }) {
  const n = lanes.length;
  return (
    <div data-testid="kc-diagram-swimlane" className="grid gap-x-1.5 gap-y-1.5 min-w-[600px]" style={{ gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))` }}>
      {lanes.map((l, i) => (
        <div key={l} className="text-center text-[10.5px] uppercase tracking-wider font-semibold text-muted pb-1.5 border-b-2 leading-tight"
             style={{ gridColumn: i + 1, gridRow: 1, borderColor: col(TONES[i % TONES.length], 0.55) }}>{l}</div>
      ))}
      {lanes.map((l, i) => <div key={`bg-${l}`} aria-hidden className="rounded-sm" style={{ gridColumn: i + 1, gridRow: `2 / ${steps.length + 2}`, background: col(TONES[i % TONES.length], 0.035) }} />)}
      {steps.map((s, i) => {
        const tone = TONES[s.lane % TONES.length];
        return (
          <div key={i} data-testid={`kc-swim-${i}`} className="border rounded-sm px-2.5 py-2 bg-bg/80 relative"
               style={{ gridColumn: `${s.lane + 1} / span ${s.span || 1}`, gridRow: i + 2, borderColor: col(tone, 0.5) }}>
            <div className="flex items-start gap-1.5">
              <span className="w-4 h-4 mt-px rounded-full font-mono-tabular text-[9px] font-bold flex items-center justify-center shrink-0" style={{ background: col(tone, 0.15), color: col(tone) }}>{i + 1}</span>
              <span className="text-[11.5px] font-semibold leading-tight">{s.title}</span>
            </div>
            {s.note && <div className="text-[10.5px] text-muted mt-1 leading-snug">{s.note}</div>}
          </div>
        );
      })}
    </div>
  );
}

// Operating rhythm: what is checked every day, week, month and quarter, and by whom.
export function Cadence({ rhythms, loop }) {
  return (
    <div data-testid="kc-diagram-cadence">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {rhythms.map((r, i) => (
          <div key={r.every} data-testid={`kc-cadence-${i}`} className="border rounded-sm p-3 bg-bg/60" style={{ borderColor: col(TONES[i % TONES.length], 0.45) }}>
            <div className="text-[12.5px] font-semibold" style={{ color: col(TONES[i % TONES.length]) }}>{r.every}</div>
            <div className="text-[10.5px] text-muted">{r.who}</div>
            <ul className="mt-2 space-y-1 text-[11px] leading-snug list-disc pl-3.5">{r.items.map(x => <li key={x}>{x}</li>)}</ul>
          </div>
        ))}
      </div>
      {loop && <div className="mt-2.5 text-[11.5px] text-muted inline-flex items-center gap-1.5"><Repeat size={12} className="text-primary" />{loop}</div>}
    </div>
  );
}
